import React from 'react';
import { Link } from 'react-router-dom';

const PRESENTER_IMAGE = '/files/images/predictorpro-des.png';

const SECTION_CLASS = 'card shadow p-4 md:p-6 bg-gray-50 dark:bg-gray-800/50';

const RULE_CARD_CLASS =
  'rounded-lg border border-gray-200 bg-white p-3 md:p-4 h-full shadow-sm dark:bg-gray-800 dark:border-gray-600';

interface ScoringRule {
  icon: string;
  title: string;
  description: string;
  points: string;
  note?: string;
}

const SCORING_RULES: ScoringRule[] = [
  {
    icon: '🥇',
    title: 'Correct Result',
    description: 'Predict the right outcome - home win, draw, or away win',
    points: '1 pt',
  },
  {
    icon: '⚽️',
    title: 'Exact Score',
    description: 'Nail the precise scoreline for maximum points',
    points: '3 pts',
  },
  {
    icon: '💷',
    title: 'Banker',
    description: 'Doubles your points if correct, but lose 1pt if wrong',
    points: '2×',
    note: 'Available once per round',
  },
];

interface ScoringExplainedProps {
  /** Show the Getting Started steps (homepage only) */
  showGettingStarted?: boolean;
  className?: string;
}

const ScoringRuleCard: React.FC<ScoringRule> = ({
  icon,
  title,
  description,
  points,
  note,
}) => (
  <div className={`flex flex-col gap-3 md:gap-2 ${RULE_CARD_CLASS}`}>
    <div className="flex md:w-full items-start md:items-center justify-between gap-2 flex-shrink-0">
      <span className="text-xl leading-none" aria-hidden="true">
        {icon}
      </span>
      <span
        className="flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full"
        style={{ backgroundColor: 'var(--highlight)', color: '#000' }}
      >
        {points}
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
        {title}
      </p>
      <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {description}
      </p>
      {note && (
        <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--highlight)' }}>
          {note}
        </p>
      )}
    </div>
  </div>
);

const GettingStartedSteps: React.FC = () => {
  const steps = [
    {
      title: 'Create or join a league',
      description: (
        <>
          <Link to="/leagues" className="font-medium hover:underline" style={{ color: 'var(--highlight)' }}>
            Create a private friends league and invite mates
          </Link>{' '}
          or join an existing one with an invite code
        </>
      ),
    },
    {
      title: 'Make your predictions',
      description: 'Submit your scorelines for the upcoming matchweek before kick-off',
    },
    {
      title: 'Climb the table',
      description: 'Top your league standings and earn the glory of best predictor!',
    },
  ];

  return (
    <section className={SECTION_CLASS}>
      <h3 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
        Getting Started
      </h3>
{/*       <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
        Three steps to start competing
      </p> */}
      <ol className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-gray-200 dark:divide-gray-600 gap-6 md:gap-0">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className={`flex flex-row md:flex-col gap-3 md:gap-2 md:px-5 ${
              index === 0 ? 'md:pl-0' : ''
            } ${index === steps.length - 1 ? 'md:pr-0' : ''}`}
          >
            <span
              className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: 'var(--highlight)', color: '#000' }}
            >
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                {step.title}
              </p>
              <p className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {step.description}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

const ScoringSection: React.FC = () => (
  <section className={SECTION_CLASS}>
    <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
      Scoring Explained
    </h3>
    <p className="text-sm mb-4 md:mb-2" style={{ color: 'var(--text-secondary)' }}>
      Here&apos;s how your predictions earn points
    </p>
    <div className="flex flex-col md:flex-row-reverse gap-4 md:gap-6 md:items-center">
      <img
        src={PRESENTER_IMAGE}
        alt="Predictor Pro Presenter"
        className="w-56 sm:w-64 md:w-48 lg:w-52 aspect-square object-contain mx-auto md:mx-0 flex-shrink-0"
      />
      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-3 md:pb-1">
        {SCORING_RULES.map((rule) => (
          <ScoringRuleCard key={rule.title} {...rule} />
        ))}
      </div>
    </div>
  </section>
);

const ScoringExplained: React.FC<ScoringExplainedProps> = ({
  showGettingStarted = false,
  className = '',
}) => (
  <div className={`space-y-6 mt-6 ${className}`}>
    {showGettingStarted && <GettingStartedSteps />}
    <ScoringSection />
  </div>
);

export default ScoringExplained;
